import { useCallback } from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { Button, Stack, TextField } from '@mui/material'

import { Section } from 'components/controls/section'
import { MarkdownInput } from 'components/controls/markDownInput'

import { TenantFormData, TenantDetailResult } from "interface/tenant"

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { convertToISODateTime } from 'shared/util'

const {
  Required,
  TooLongLength,
  TooShortLength,
	InvalidURL,
	InvalidEMailAddress
} = validationMessages

const formSchema = yup.object().shape({
  tenantName: yup
		.string()
		.required(Required)
		.min(1, TooShortLength(1))
		.max(20, TooLongLength(20)),
  manualURL: yup
		.string()
		.nullable()
		.max(512, TooLongLength(512))
		.url(InvalidURL),
	adminAddress: yup
		.string()
		.nullable()
		.max(512, TooLongLength(512))
		.email(InvalidEMailAddress),
  replyAddress: yup
		.string()
		.nullable()
		.max(512, TooLongLength(512))
		.email(InvalidEMailAddress),
  supportAddress: yup
		.string()
		.nullable()
		.max(512, TooLongLength(512))
		.email(InvalidEMailAddress),
  leadText: yup
		.string()
		.nullable(),
  signature: yup
		.string()
		.nullable(),
  effectiveTime: yup
		.string()
		.nullable()
})

type useTenantFormProps = {
	tenant: TenantDetailResult | null
	onSubmit: (form: TenantFormData) => void
}
export const useTenantForm = ({
	tenant,
	onSubmit
}: useTenantFormProps) => {
	const form = useForm({
		defaultValues: tenant ? {
			tenantName: tenant.tenantName,
			manualURL: tenant.manualURL,
			adminAddress: tenant.adminAddress,
			replyAddress: tenant.replyAddress,
			supportAddress: tenant.supportAddress,
			leadText: tenant.leadText,
			signature: tenant.signature,
			effectiveTime: tenant.effectiveTime
		} : {
			tenantName: '',
			manualURL: null,
			adminAddress: null,
			replyAddress: null,
			supportAddress: null,
			leadText: null,
			signature: null,
			effectiveTime: null
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
    submit: form.handleSubmit(input => onSubmit({
			tenantName: input.tenantName,
			manualURL: input.manualURL ?? null,
			adminAddress: input.adminAddress ?? null,
			replyAddress: input.replyAddress ?? null,
			supportAddress: input.supportAddress ?? null,
			leadText: input.leadText ?? null,
			signature: input.signature ?? null,
			effectiveTime: convertToISODateTime(input.effectiveTime)
		}))
	}
}

export const TenantForm = ({
	control,
	setValue,
	formState: { errors }
}: ReturnType<typeof useTenantForm>) => {
	const clearEffectiveTime = useCallback(
		() => {
			setValue("effectiveTime", null)
		},
		[setValue]
	)
	return (
		<Stack direction="column" flexGrow={1} overflow="hidden">
			<Section label="テナント名" labelProps={{ py: 2 }}>
				<Controller
					control={control}
					name="tenantName"
					render={({ field }) => (
						<TextField
							{...field}
							variant="outlined"
							size="small"
							error={!!errors.tenantName}
							helperText={errors.tenantName ? errors.tenantName.message : ''}
						/>
					)}
				/>
			</Section>

			<Section label="リソース" labelProps={{ py: 2 }}>
				<Section label="マニュアル" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="manualURL"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.manualURL}
								helperText={errors.manualURL ? errors.manualURL.message : ''}
							/>
						)}
					/>
				</Section>
			</Section>

			<Section label="メールアドレス" labelProps={{ py: 2 }}>
				<Section label="通知先" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="adminAddress"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.adminAddress}
								helperText={errors.adminAddress ? errors.adminAddress.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="送信元" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="replyAddress"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.replyAddress}
								helperText={errors.replyAddress ? errors.replyAddress.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="サポート" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="supportAddress"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.supportAddress}
								helperText={errors.supportAddress ? errors.supportAddress.message : ''}
							/>
						)}
					/>
				</Section>
			</Section>

			<Section label="メール内容" labelProps={{ py: 2 }}>
				<Section label="冒頭文" labelProps={{ py: 2 }} flexGrow={1} overflow="hidden">
					<Controller
						control={control}
						name="leadText"
						render={({ field }) => (
							<MarkdownInput
								{...field}
								variant="outlined"
								size="small"
								rows={3}
								error={!!errors.leadText}
								helperText={errors.leadText ? errors.leadText.message : ''}
							/>
						)}
					/>
				</Section>
				<Section label="署名" labelProps={{ py: 2 }} flexGrow={1} overflow="hidden">
					<Controller
						control={control}
						name="signature"
						render={({ field }) => (
							<MarkdownInput
								{...field}
								variant="outlined"
								size="small"
								rows={3}
								error={!!errors.signature}
								helperText={errors.signature ? errors.signature.message : ''}
							/>
						)}
					/>
				</Section>
			</Section>

			<Section label="有効期限" labelProps={{ py: 2 }} disablePaddingY>
				<Stack direction="row" spacing={1}>
					<Controller
						name="effectiveTime"
						control={control}
						rules={{
							required: false,
							validate: (value) => {
								const formatDate = dayjs(value).format('YYYY/MM/DD HH:mm')
								if (!dayjs(formatDate).isValid()) {
									return '日時形式が間違っています'
								}
							}
						}}
						render={({ field, formState: { errors } }) => (
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale="ja"
							>
								<DesktopDatePicker
									{...field}
									format="YYYY/MM/DD HH:mm"
									value={dayjs(field.value)}
									slotProps={{
										textField: {
											size: "small",
											variant: "outlined",
											error: !!errors.effectiveTime,
											helperText: errors.effectiveTime ? errors.effectiveTime.message : ''
										}
									}}
								/>
							</LocalizationProvider>
						)}
					/>
					<Button
						size="small"
						onClick={clearEffectiveTime}
					>
						クリア
					</Button>
				</Stack>
			</Section>
		</Stack>
	)
}